import React, { useEffect, useState, useCallback } from 'react';
import DataTable from "react-data-table-component";
import DataTableExtensions from "react-data-table-component-extensions";
import "react-data-table-component-extensions/dist/index.css";
import axios from 'axios';
import { useNavigate } from 'react-router-dom';

export default function ZonesList() {
    const API_URL = process.env.REACT_APP_API_URL;
    const navigate = useNavigate();
    
    // State variables
    const [zones, setZones] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [searchQuery, setSearchQuery] = useState('');
    const [statusFilter, setStatusFilter] = useState('all'); // 'all', 'active', 'inactive'
    const [selectedRows, setSelectedRows] = useState([]);
    const [toggleCleared, setToggleCleared] = useState(false);
    const [deleteModalOpen, setDeleteModalOpen] = useState(false);
    const [zoneToDelete, setZoneToDelete] = useState(null);
    const [bulkDeleteModalOpen, setBulkDeleteModalOpen] = useState(false);

    // Fetch all zones on component mount
    useEffect(() => {
        fetchZones();
    }, [statusFilter]);

    // Fetch zones from API
    const fetchZones = async () => {
        setLoading(true);
        try {
            let endpoint = `${API_URL}shypvdriverapp/api/pricing/zones/getAll`;
            
            if (statusFilter === 'active') {
                endpoint = `${API_URL}shypvdriverapp/api/pricing/zones/getActive`;
            } else if (searchQuery) {
                endpoint = `${API_URL}shypvdriverapp/api/pricing/zones/search?q=${encodeURIComponent(searchQuery)}`;
            }
            
            const response = await axios.get(endpoint);
            
            if (response.data.success) {
                let zonesData = response.data.data || [];
                
                // Apply status filter for 'inactive' since API doesn't have endpoint for it
                if (statusFilter === 'inactive') {
                    zonesData = zonesData.filter(zone => !zone.isActive);
                }
                
                setZones(zonesData);
            } else {
                setError('Failed to fetch zones');
            }
        } catch (err) {
            console.error('Error fetching zones:', err);
            setError('Error fetching zones. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    // Search zones
    const handleSearch = async () => {
        if (!searchQuery.trim()) {
            fetchZones();
            return;
        }
        
        setLoading(true);
        try {
            const response = await axios.get(`${API_URL}shypvdriverapp/api/pricing/zones/search?q=${encodeURIComponent(searchQuery)}`);
            
            if (response.data.success) {
                setZones(response.data.data || []);
            }
        } catch (err) {
            console.error('Error searching zones:', err);
        } finally {
            setLoading(false);
        }
    };

    // Delete single zone
    const handleDeleteZone = async () => {
        if (!zoneToDelete) return;
        
        try {
            const response = await axios.delete(`${API_URL}shypvdriverapp/api/pricing/zones/delete?id=${zoneToDelete.zoneId}`);
            
            if (response.data.success) {
                setZones(zones.filter(z => z.zoneId !== zoneToDelete.zoneId));
                alert('Zone deleted successfully');
            } else {
                alert('Failed to delete zone');
            }
        } catch (err) {
            console.error('Error deleting zone:', err);
            alert('Error deleting zone. Please try again.');
        } finally {
            setDeleteModalOpen(false);
            setZoneToDelete(null);
        }
    };

    // Bulk delete zones
    const handleBulkDelete = async () => {
        if (selectedRows.length === 0) return;
        
        try {
            const zoneIds = selectedRows.map(row => row.zoneId);
            const response = await axios.delete(`${API_URL}shypvdriverapp/api/pricing/zones/deleteMultiple`, {
                data: { zoneIds }
            });
            
            if (response.data.success) {
                setZones(zones.filter(z => !zoneIds.includes(z.zoneId)));
                setSelectedRows([]);
                setToggleCleared(!toggleCleared);
                alert('Zones deleted successfully');
            } else {
                alert('Failed to delete zones');
            }
        } catch (err) {
            console.error('Error deleting zones:', err);
            alert('Error deleting zones. Please try again.');
        } finally {
            setBulkDeleteModalOpen(false);
        }
    };

    // Handle row selection
    const handleRowSelected = useCallback(state => {
        setSelectedRows(state.selectedRows);
    }, []);

    // Navigate to create zone page
    const handleAddZone = () => {
        navigate('/orders/zones/create');
    };

    // Navigate to edit zone page
    const handleEditZone = (zone) => {
        navigate(`/orders/zones/edit/${zone.zoneId}`);
    };

    // Open delete confirmation modal
    const openDeleteModal = (zone) => {
        setZoneToDelete(zone);
        setDeleteModalOpen(true);
    };

    // DataTable columns
    const columns = [
        {
            name: 'Zone Code',
            selector: row => row.zoneCode,
            sortable: true,
            cell: row => (
                <span className="zone-code-cell">{row.zoneCode}</span>
            ),
        },
        {
            name: 'Zone Name',
            selector: row => row.zoneName,
            sortable: true,
            cell: row => (
                <div style={{ wordWrap: 'break-word', whiteSpace: 'normal', textAlign: 'left' }}>
                    {row.zoneName}
                </div>
            ),
        },
        {
            name: 'City',
            selector: row => row.city || '-',
            sortable: true,
        },
        {
            name: 'Country',
            selector: row => row.country || '-',
            sortable: true,
        },
        {
            name: 'Region',
            selector: row => row.region || '-',
            sortable: true,
        },
        {
            name: 'Status',
            selector: row => row.isActive,
            sortable: true,
            cell: row => (
                <span style={{
                    ...styles.statusBadge,
                    ...(row.isActive ? styles.statusActive : styles.statusInactive)
                }}>
                    {row.isActive ? 'Active' : 'Inactive'}
                </span>
            ),
        },
        {
            name: 'Coordinates',
            selector: row => row.coordinates?.length || 0,
            sortable: true,
            cell: row => (
                <span style={{ fontSize: '13px', color: '#6b7280' }}>
                    {row.coordinates?.length || 0} points
                </span>
            ),
        },
        {
            name: 'Actions',
            cell: row => (
                <div style={styles.actionButtons}>
                    <button 
                        style={{ ...styles.btnAction, ...styles.btnEdit }}
                        onClick={() => handleEditZone(row)}
                        title="Edit Zone"
                    >
                        Edit
                    </button>
                    <button 
                        style={{ ...styles.btnAction, ...styles.btnDelete }}
                        onClick={() => openDeleteModal(row)}
                        title="Delete Zone"
                    >
                        Delete
                    </button>
                </div>
            ),
            ignoreRowClick: true,
            allowOverflow: true,
            button: true,
        },
    ];

    // Custom styles for DataTable
    const customStyles = {
        headRow: {
            style: {
                backgroundColor: '#f8f9fa',
                borderBottom: '2px solid #dee2e6',
            },
        },
        headCells: {
            style: {
                fontWeight: '600',
                fontSize: '14px',
                color: '#495057',
            },
        },
        rows: {
            style: {
                minHeight: '52px',
                '&:hover': {
                    backgroundColor: '#f1f3f4',
                },
            },
        },
    };

    // Table data for extensions
    const tableData = {
        columns,
        data: zones,
    };

    // Inline styles
    const styles = {
        container: {
            padding: '24px 40px',
            backgroundColor: '#f9fafb',
            minHeight: '100vh',
        },
        header: {
            marginBottom: '32px',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'flex-start',
        },
        headerLeft: {
            flex: 1,
        },
        title: {
            margin: 0,
            fontSize: '28px',
            fontWeight: '700',
            color: '#111827',
            marginBottom: '8px',
        },
        subtitle: {
            margin: 0,
            fontSize: '14px',
            color: '#6b7280',
        },
        btnPrimary: {
            padding: '12px 24px',
            backgroundColor: '#4f46e5',
            color: '#fff',
            border: 'none',
            borderRadius: '8px',
            fontSize: '14px',
            fontWeight: '600',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            transition: 'all 0.2s ease',
            boxShadow: '0 2px 4px rgba(79, 70, 229, 0.2)',
        },
        filtersSection: {
            backgroundColor: '#fff',
            borderRadius: '12px',
            padding: '20px',
            marginBottom: '24px',
            boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1)',
            display: 'flex',
            gap: '16px',
            alignItems: 'center',
            flexWrap: 'wrap',
        },
        searchBox: {
            display: 'flex',
            flex: 1,
            minWidth: '300px',
            gap: '8px',
        },
        searchInput: {
            flex: 1,
            padding: '10px 16px',
            border: 'none',
            borderRadius: '8px',
            fontSize: '14px',
            backgroundColor: '#f3f4f6',
            outline: 'none',
            transition: 'all 0.2s ease',
        },
        btnSearch: {
            padding: '10px 20px',
            backgroundColor: '#4f46e5',
            color: '#fff',
            border: 'none',
            borderRadius: '8px',
            cursor: 'pointer',
            fontSize: '14px',
            fontWeight: '500',
            transition: 'all 0.2s ease',
        },
        filterDropdown: {
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
        },
        filterLabel: {
            fontSize: '14px',
            fontWeight: '500',
            color: '#374151',
        },
        select: {
            padding: '10px 16px',
            border: 'none',
            borderRadius: '8px',
            fontSize: '14px',
            backgroundColor: '#f3f4f6',
            cursor: 'pointer',
            outline: 'none',
            minWidth: '120px',
        },
        bulkActions: {
            display: 'flex',
            alignItems: 'center',
            gap: '12px',
            padding: '8px 16px',
            backgroundColor: '#fef3c7',
            borderRadius: '8px',
        },
        selectedCount: {
            fontSize: '14px',
            fontWeight: '600',
            color: '#92400e',
        },
        btnDanger: {
            padding: '8px 16px',
            backgroundColor: '#dc2626',
            color: '#fff',
            border: 'none',
            borderRadius: '6px',
            fontSize: '13px',
            fontWeight: '600',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: '6px',
        },
        tableContainer: {
            backgroundColor: '#fff',
            borderRadius: '12px',
            boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1)',
            overflow: 'hidden',
        },
        modalOverlay: {
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
            animation: 'fadeIn 0.2s ease',
        },
        modalContent: {
            backgroundColor: '#fff',
            borderRadius: '12px',
            width: '90%',
            maxWidth: '500px',
            boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1)',
            animation: 'slideUp 0.3s ease',
        },
        modalHeader: {
            padding: '20px 24px',
            borderBottom: '1px solid #e5e7eb',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
        },
        modalTitle: {
            margin: 0,
            fontSize: '18px',
            fontWeight: '600',
            color: '#111827',
        },
        closeBtn: {
            width: '32px',
            height: '32px',
            borderRadius: '50%',
            border: 'none',
            background: 'transparent',
            cursor: 'pointer',
            fontSize: '20px',
            color: '#6b7280',
            transition: 'all 0.2s ease',
        },
        modalBody: {
            padding: '24px',
        },
        warningText: {
            marginTop: '12px',
            padding: '12px',
            backgroundColor: '#fef3c7',
            borderRadius: '8px',
            fontSize: '13px',
            color: '#92400e',
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
        },
        modalFooter: {
            padding: '16px 24px',
            borderTop: '1px solid #e5e7eb',
            display: 'flex',
            justifyContent: 'flex-end',
            gap: '12px',
        },
        btnSecondary: {
            padding: '10px 24px',
            backgroundColor: '#fff',
            color: '#374151',
            border: '1px solid #d1d5db',
            borderRadius: '8px',
            fontSize: '14px',
            fontWeight: '600',
            cursor: 'pointer',
        },
        alert: {
            padding: '16px',
            backgroundColor: '#fee2e2',
            borderRadius: '8px',
            marginBottom: '20px',
            color: '#dc2626',
            display: 'flex',
            alignItems: 'center',
            gap: '12px',
            fontSize: '14px',
        },
        statusBadge: {
            padding: '4px 12px',
            borderRadius: '12px',
            fontSize: '12px',
            fontWeight: '600',
            display: 'inline-block',
        },
        statusActive: {
            backgroundColor: '#d1fae5',
            color: '#065f46',
        },
        statusInactive: {
            backgroundColor: '#fee2e2',
            color: '#991b1b',
        },
        actionButtons: {
            display: 'flex',
            gap: '8px',
        },
        btnAction: {
            padding: '6px 12px',
            border: 'none',
            borderRadius: '6px',
            cursor: 'pointer',
            fontSize: '13px',
            fontWeight: '500',
            transition: 'all 0.2s ease',
        },
        btnEdit: {
            backgroundColor: '#dbeafe',
            color: '#1e40af',
        },
        btnDelete: {
            backgroundColor: '#fee2e2',
            color: '#dc2626',
        },
        noData: {
            padding: '60px 20px',
            textAlign: 'center',
        },
        noDataText: {
            fontSize: '16px',
            color: '#6b7280',
            marginTop: '16px',
            marginBottom: '24px',
        },
    };

    return (
        <div style={styles.container}>
            <style>
                {`
                    @keyframes fadeIn {
                        from { opacity: 0; }
                        to { opacity: 1; }
                    }
                    @keyframes slideUp {
                        from {
                            opacity: 0;
                            transform: translateY(20px);
                        }
                        to {
                            opacity: 1;
                            transform: translateY(0);
                        }
                    }
                    .rdt_TableHead {
                        background-color: #f9fafb !important;
                    }
                    .rdt_TableHeadRow {
                        border-bottom: 2px solid #e5e7eb !important;
                        background-color: #f9fafb !important;
                    }
                    .rdt_TableRow:hover {
                        background-color: #f3f4f6 !important;
                    }
                `}
            </style>

            {/* Page Header */}
            <div style={styles.header}>
                <div style={styles.headerLeft}>
                    <h1 style={styles.title}>Zones Management</h1>
                    <p style={styles.subtitle}>Manage delivery zones and geographic boundaries</p>
                </div>
                <button 
                    style={styles.btnPrimary} 
                    onClick={handleAddZone}
                    onMouseEnter={(e) => {
                        e.currentTarget.style.backgroundColor = '#4338ca';
                        e.currentTarget.style.boxShadow = '0 4px 6px rgba(79, 70, 229, 0.3)';
                    }}
                    onMouseLeave={(e) => {
                        e.currentTarget.style.backgroundColor = '#4f46e5';
                        e.currentTarget.style.boxShadow = '0 2px 4px rgba(79, 70, 229, 0.2)';
                    }}
                >
                    <span style={{ fontSize: '16px' }}>+</span>
                    Add New Zone
                </button>
            </div>

            {/* Filters and Search */}
            <div style={styles.filtersSection}>
                <div style={styles.searchBox}>
                    <input
                        type="text"
                        placeholder="Search zones by name, code, city..."
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                        style={styles.searchInput}
                        onFocus={(e) => {
                            e.currentTarget.style.backgroundColor = '#ffffff';
                            e.currentTarget.style.boxShadow = '0 0 0 3px rgba(79, 70, 229, 0.1)';
                        }}
                        onBlur={(e) => {
                            e.currentTarget.style.backgroundColor = '#f3f4f6';
                            e.currentTarget.style.boxShadow = 'none';
                        }}
                    />
                    <button style={styles.btnSearch} onClick={handleSearch}>
                        Search
                    </button>
                </div>
                
                <div style={styles.filterDropdown}>
                    <label style={styles.filterLabel}>Status:</label>
                    <select 
                        value={statusFilter} 
                        onChange={(e) => setStatusFilter(e.target.value)}
                        style={styles.select}
                    >
                        <option value="all">All</option>
                        <option value="active">Active</option>
                        <option value="inactive">Inactive</option>
                    </select>
                </div>

                {selectedRows.length > 0 && (
                    <div style={styles.bulkActions}>
                        <span style={styles.selectedCount}>{selectedRows.length} selected</span>
                        <button 
                            style={styles.btnDanger}
                            onClick={() => setBulkDeleteModalOpen(true)}
                        >
                            Delete Selected
                        </button>
                    </div>
                )}
            </div>

            {/* Error Message */}
            {error && (
                <div style={styles.alert}>
                    <span style={{ fontSize: '18px' }}>⚠</span>
                    {error}
                </div>
            )}

            {/* DataTable */}
            <div style={styles.tableContainer}>
                <DataTableExtensions {...tableData} print={false} export={true} filterHidden={true}>
                    <DataTable
                        columns={columns}
                        data={zones}
                        pagination
                        paginationPerPage={10}
                        paginationRowsPerPageOptions={[10, 25, 50, 100]}
                        selectableRows
                        selectableRowsHighlight
                        onSelectedRowsChange={handleRowSelected}
                        clearSelectedRows={toggleCleared}
                        progressPending={loading}
                        progressComponent={
                            <div style={{ padding: '40px', textAlign: 'center', color: '#6b7280' }}>
                                Loading zones...
                            </div>
                        }
                        noDataComponent={
                            <div style={styles.noData}>
                                <span style={{ fontSize: '48px' }}>📍</span>
                                <p style={styles.noDataText}>No zones found</p>
                                <button style={styles.btnPrimary} onClick={handleAddZone}>
                                    Create First Zone
                                </button>
                            </div>
                        }
                        customStyles={customStyles}
                        highlightOnHover
                        pointerOnHover
                    />
                </DataTableExtensions>
            </div>

            {/* Delete Confirmation Modal */}
            {deleteModalOpen && (
                <div style={styles.modalOverlay}>
                    <div style={styles.modalContent}>
                        <div style={styles.modalHeader}>
                            <h3 style={styles.modalTitle}>Confirm Delete</h3>
                            <button 
                                style={styles.closeBtn} 
                                onClick={() => setDeleteModalOpen(false)}
                                onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f3f4f6'}
                                onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                            >
                                ×
                            </button>
                        </div>
                        <div style={styles.modalBody}>
                            <p style={{ fontSize: '14px', color: '#374151', marginBottom: '12px' }}>
                                Are you sure you want to delete zone <strong>"{zoneToDelete?.zoneName}"</strong>?
                            </p>
                            <div style={styles.warningText}>
                                <span>⚠</span>
                                This action cannot be undone.
                            </div>
                        </div>
                        <div style={styles.modalFooter}>
                            <button style={styles.btnSecondary} onClick={() => setDeleteModalOpen(false)}>
                                Cancel
                            </button>
                            <button style={styles.btnDanger} onClick={handleDeleteZone}>
                                Delete Zone
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Bulk Delete Confirmation Modal */}
            {bulkDeleteModalOpen && (
                <div style={styles.modalOverlay}>
                    <div style={styles.modalContent}>
                        <div style={styles.modalHeader}>
                            <h3 style={styles.modalTitle}>Confirm Bulk Delete</h3>
                            <button 
                                style={styles.closeBtn} 
                                onClick={() => setBulkDeleteModalOpen(false)}
                                onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f3f4f6'}
                                onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                            >
                                ×
                            </button>
                        </div>
                        <div style={styles.modalBody}>
                            <p style={{ fontSize: '14px', color: '#374151', marginBottom: '12px' }}>
                                Are you sure you want to delete <strong>{selectedRows.length}</strong> selected zones?
                            </p>
                            <div style={styles.warningText}>
                                <span>⚠</span>
                                This action cannot be undone.
                            </div>
                        </div>
                        <div style={styles.modalFooter}>
                            <button style={styles.btnSecondary} onClick={() => setBulkDeleteModalOpen(false)}>
                                Cancel
                            </button>
                            <button style={styles.btnDanger} onClick={handleBulkDelete}>
                                Delete {selectedRows.length} Zones
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
